import type { IDistributionRepository } from "~/interfaces/IDistributionRepository";
import { DistributionInfo, type DistributionType } from "~/entities/DistributionInfo";
import { RelatedContentsItem } from "~/entities/RelatedContentsItem";

export class DistributionRepository implements IDistributionRepository {
  /**
   * 関連コンテンツ一覧取得
   */
  async getRelatedContents(contentsId: string): Promise<RelatedContentsItem[]> {
    const res = await GetRequest<{ statusCode: number; contents: any[] }>(
      `/contents_manage/${contentsId}/related_contents`,
    );
    if (!res || res.statusCode !== 200) {
      throw new Error("Failed to getRelatedContents");
    }
    return res.contents;
  }

  /**
   * 配信用のコンテンツファイルをアップロードする
   */
  async uploadContentFile(file: File): Promise<{ fileKey: string }> {
    return await this.uploadFile(`/contents_manage/contents/presigned_url`, file);
  }

  /**
   * 配信用のサムネイルファイルをアップロードする
   */
  async uploadThumbnailFile(file: File): Promise<{ fileKey: string }> {
    return await this.uploadFile(`/contents_manage/thumbnail/presigned_url`, file);
  }

  /**
   * 配信コンテンツを投稿する
   */
  async postDistribution(distributionInfo: DistributionInfo): Promise<{ contentsId: string }> {
    const contentType = this.getContentType(distributionInfo.type);

    const distributeAt =
      (distributionInfo.type === "live" || distributionInfo.type === "article") && !distributionInfo.distributeNow
        ? distributionInfo.distributeAt
        : null;

    const res = await PostRequest<{ statusCode: number; contentsId: string }>(
      `/contents_manage`,
      JSON.stringify({
        contents_type: contentType,
        plan_id: distributionInfo.planId,
        is_free: distributionInfo.isFree,
        featured_flag: distributionInfo.isFeatured,
        title: distributionInfo.title,
        description: distributionInfo.detail,
        contents_key: distributionInfo.contentFile.fileKey,
        thumbnail_key: distributionInfo.thumbnailFile.fileKey,
        tags: distributionInfo.tags.map(tag => Number(tag.id)),
        library_ids: distributionInfo.libraries.map(library => library.libraryId),
        start_date: distributeAt ? this.convertDateString(distributeAt) : null, // 今すぐ配信の場合はnullを入れる
        movie_play_time_sec: distributionInfo.moviePlayTimeSec,
        publish_status: distributionInfo.publishStatus,
        post_user_id: distributionInfo.postUserId,
        article_body: distributionInfo.articleBody,
        article_file_keys: distributionInfo.articleFiles.map(file => file.fileKey),
        article_status: distributionInfo.articleStatus,
      }),
    );
    if (!res || res.statusCode !== 200) {
      throw new Error("Failed to post distribution");
    }
    return { contentsId: res.contentsId };
  }

  /**
   * 配信コンテンツを変更する
   */
  async putDistribution(distributionInfo: DistributionInfo): Promise<void> {
    const contentType = this.getContentType(distributionInfo.type);
    const thumbnailKey = distributionInfo.thumbnailFile.fileKey.includes("http")
      ? distributionInfo.thumbnailKey
      : distributionInfo.thumbnailFile.fileKey;
    const distributeAt =
      (distributionInfo.type === "live" || distributionInfo.type === "article") && !distributionInfo.distributeNow
        ? distributionInfo.distributeAt
        : null;
    const res = await PutRequest<{ statusCode: number }>(
      `/contents_manage/${distributionInfo.contentsId}`,
      JSON.stringify({
        contents_type: contentType,
        plan_id: distributionInfo.planId,
        is_free: distributionInfo.isFree,
        featured_flag: distributionInfo.isFeatured,
        title: distributionInfo.title,
        description: distributionInfo.detail,
        contents_key: distributionInfo.contentFile.fileKey,
        thumbnail_key: thumbnailKey,
        tags: distributionInfo.tags.map(tag => Number(tag.id)),
        library_ids: distributionInfo.libraries.map(library => library.libraryId),
        start_date: distributeAt ? this.convertDateString(distributeAt) : null,
        publish_status: distributionInfo.publishStatus,
        post_user_id: distributionInfo.postUserId,
        article_body: distributionInfo.articleBody,
        article_file_keys: distributionInfo.articleFiles.map(file => file.fileKey),
        article_status: distributionInfo.articleStatus,
      }),
      true,
      true,
    );
    if (!res || res.statusCode !== 200) {
      throw new Error("Failed to put distribution");
    }
  }

  /**
   * 配信コンテンツを削除する
   */
  async deleteDistribution(distributionInfo: DistributionInfo): Promise<void> {
    const res = await DeleteRequest<{ statusCode: number; contentsId: string }>(
      `/contents_manage/${distributionInfo.contentsId}`,
      true,
      true,
    );
    if (!res || res.statusCode !== 200) {
      throw new Error("Failed to delete distribution");
    }
  }

  /**
   * 削除した配信コンテンツを復旧する
   */
  async recoverDistribution(distributionInfo: DistributionInfo): Promise<void> {
    const res = await PutRequest<{ statusCode: number; contentsId: string }>(
      `/contents_manage/${distributionInfo.contentsId}/cancel_delete`,
      "",
    );
    if (!res || res.statusCode !== 200) {
      throw new Error("Failed to recoverDistribution");
    }
  }

  /*
   * コンテンツ取得用の署名付きURLを返却する
   */
  async getSignedUrl(fileKey: string): Promise<{ fileUrl: string; fileSubUrl: string }> {
    return await GetRequest<{ fileUrl: string; fileSubUrl: string }>(
      `/contents_manage/article_file?contents_key=${fileKey}`,
      false,
      true,
    );
  }

  private async uploadFile(apiPath: string, file: File) {
    // 署名付きS3アップロードURLを取得する
    const mimeType = file.type;
    const presignedRes = await GetRequest<{
      statusCode: number;
      presignedUrl: string;
      fileKey: string;
    }>(`${apiPath}?mime_type=${mimeType}`);
    if (!presignedRes || presignedRes.statusCode !== 200) {
      throw new Error("Failed get presigned_url");
    }

    // S3 にアップロード
    const s3Res = await PutS3Request(presignedRes.presignedUrl, file);
    if (!s3Res || s3Res.statusCode !== 200) {
      throw new Error("Failed to put s3");
    }

    const fileKey = presignedRes.fileKey;
    return { fileKey };
  }

  private getContentType(distributionType: DistributionType) {
    switch (distributionType) {
      case "live":
        return "schedule";
      case "livearchive":
        return "live_movie";
      case "movie":
        return "movie";
      case "document":
        return "document";
      case "article":
        return "article";
    }
  }

  private convertDateString(dateString: string) {
    return new Date(dateString).toISOString();
  }
}
